import React, { useState, useCallback, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';

interface MarkerResult {
  id: number;
  reportId: number;
  reportFilename: string;
  testDate: string | null;
  value: number;
  units: string;
  status: string | null;
  referenceMin: number | null;
  referenceMax: number | null;
  confidence: number | null;
}

interface EditState {
  id: number;
  value: string;
  units: string;
}

interface MarkerSearchViewProps {
  onBack: () => void;
  initialSearchTerm?: string;
}

export const MarkerSearchView: React.FC<MarkerSearchViewProps> = ({ onBack, initialSearchTerm = '' }) => {
  const { user } = useSimpleAuth();
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [searchResults, setSearchResults] = useState<Record<string, MarkerResult[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingState, setEditingState] = useState<EditState | null>(null);

  // Perform search
  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim() || !user?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get(`/api/markers/search?search=${encodeURIComponent(searchTerm)}`);

      if (response.status === 'success') {
        setSearchResults(response.data || {});
      }
    } catch (error) {
      console.error('Error searching markers:', error);
      setError('Failed to search markers');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, user]);

  // Handle edit
  const handleEdit = (result: MarkerResult) => {
    setEditingState({
      id: result.id,
      value: result.value.toString(),
      units: result.units
    });
  };

  // Save edit
  const handleSave = async () => {
    if (!user?.id || !editingState) return;

    try {
      const response = await apiService.put(`/api/results/${editingState.id}`, {
        value: parseFloat(editingState.value),
        units: editingState.units
      });

      if (response.status === 'success') {
        // Update local state
        const newResults = { ...searchResults };
        for (const markerName in newResults) {
          newResults[markerName] = newResults[markerName].map(result => 
            result.id === editingState.id 
              ? { ...result, value: parseFloat(editingState.value), units: editingState.units }
              : result
          );
        }
        setSearchResults(newResults);
        setEditingState(null);
      }
    } catch (error) {
      console.error('Error updating value:', error);
      alert('Failed to update value');
    }
  };

  // Cancel edit
  const handleCancel = () => {
    setEditingState(null);
  };

  // Handle enter key for search
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Auto-search when component mounts with initial search term
  useEffect(() => {
    if (initialSearchTerm) {
      handleSearch();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Get status color and background
  const getStatusStyle = (status: string | null) => {
    switch (status) {
      case 'HIGH':
        return { text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' };
      case 'LOW':
        return { text: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' };
      case 'NORMAL':
        return { text: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' };
      default:
        return { text: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200' };
    }
  };

  // Calculate trend
  const calculateTrend = (results: MarkerResult[]) => {
    if (results.length < 2) return null;
    const sortedResults = [...results].sort((a, b) => {
      if (!a.testDate || !b.testDate) return 0;
      return new Date(a.testDate).getTime() - new Date(b.testDate).getTime();
    });
    const lastTwo = sortedResults.slice(-2);
    const diff = lastTwo[1].value - lastTwo[0].value;
    const percentChange = (diff / lastTwo[0].value) * 100;
    return { diff, percentChange };
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Go back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Search Lab Markers</h1>
              <p className="text-sm text-gray-600 mt-1">Find and edit specific lab results across all your reports</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search bar */}
        <div className="mb-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search for a marker (e.g., Hemoglobin, Cholesterol, Glucose)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500"
            />
            <button
              onClick={handleSearch}
              disabled={!searchTerm.trim() || loading}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <span className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors text-sm font-medium">
                Search
              </span>
            </button>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Searching...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            {error}
          </div>
        )}

        {/* Results */}
        {!loading && !error && Object.keys(searchResults).length > 0 && (
          <div className="space-y-8">
            {Object.entries(searchResults).map(([markerName, results]) => {
              const trend = calculateTrend(results);
              return (
                <div key={markerName} className="bg-white shadow-sm rounded-xl border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">{markerName}</h2>
                        <p className="text-sm text-gray-600 mt-1">
                          {results.length} result{results.length !== 1 ? 's' : ''} found
                        </p>
                      </div>
                      {trend && (
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Trend</div>
                          <div className={`flex items-center gap-1 ${trend.diff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {trend.diff > 0 ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                              </svg>
                            )}
                            <span className="font-medium">{Math.abs(trend.percentChange).toFixed(1)}%</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="divide-y divide-gray-100">
                    {results.sort((a, b) => {
                      if (!a.testDate || !b.testDate) return 0;
                      return new Date(b.testDate).getTime() - new Date(a.testDate).getTime();
                    }).map((result) => {
                      const statusStyle = getStatusStyle(result.status);
                      const isEditing = editingState?.id === result.id;
                      
                      return (
                        <div key={result.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {formatDate(result.testDate)}
                                </span>
                                <span className="text-sm text-gray-500">•</span>
                                <span className="text-sm text-gray-600">
                                  {result.reportFilename}
                                </span>
                              </div>
                              
                              {isEditing ? (
                                <div className="flex items-end gap-3">
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Value</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={editingState.value}
                                      onChange={(e) => setEditingState({ ...editingState, value: e.target.value })}
                                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      autoFocus
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Units</label>
                                    <input
                                      type="text"
                                      value={editingState.units}
                                      onChange={(e) => setEditingState({ ...editingState, units: e.target.value })}
                                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                  </div>
                                  <button
                                    onClick={handleSave}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancel}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-4">
                                  <div className="flex items-baseline gap-2">
                                    <span className="text-2xl font-semibold text-gray-900">
                                      {result.value}
                                    </span>
                                    <span className="text-sm text-gray-600">
                                      {result.units}
                                    </span>
                                  </div>
                                  
                                  {result.status && (
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border} border`}>
                                      {result.status}
                                    </span>
                                  )}
                                  
                                  {result.referenceMin !== null && result.referenceMax !== null && (
                                    <span className="text-sm text-gray-500">
                                      Ref: {result.referenceMin}-{result.referenceMax} {result.units}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {!isEditing && (
                              <button
                                onClick={() => handleEdit(result)}
                                className="px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1 text-sm font-medium"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* No results */}
        {!loading && !error && searchTerm && Object.keys(searchResults).length === 0 && (
          <div className="bg-white shadow-sm rounded-xl border border-gray-200 p-12 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-600 text-lg">No markers found matching "{searchTerm}"</p>
            <p className="text-gray-500 text-sm mt-2">Try searching with a different term</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarkerSearchView;